import dayjs from 'dayjs'
import { useState, useEffect, useContext } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

import { ESTADO_PUBLICADA } from '../../constants/publicaciones'
import { esMensajeDeMiEmpresa } from '../../helpers/esMensajeDeMiEmpresa'
import { useAuth } from '../../hooks/useAuth'
import { useReglasPublicacion } from '../../hooks/useReglasPublicacion.js'
import { setCategoriaPath } from '../../slices/filterSlice'
import { Spinner } from '../../ui/elements/Spinner/Spinner.jsx'
import { CarritoContext } from '../context/carritoContext'
import CalificacionProducto from '../publicaciones/calificacionProducto/CalificacionProducto'
import Descripcion from '../publicaciones/Descripcion'
import DetalleCabecera from '../publicaciones/DetalleCabecera'
import InformacionVendedor from '../publicaciones/InformacionVendedor'
import { MensajeriaNew } from '../reutilizables/mensajeria/MensajeriaNew'
import * as calificacionService from '../servicios/calificacionService'
import * as datosFiscalesService from '../servicios/datosFiscalesService'
import * as provinciasService from '../servicios/fetchProvinciasAPI'
import * as mensajesService from '../servicios/mensajesService'
import * as publicacionesService from '../servicios/publicacionesService'
import * as reputacionService from '../servicios/reputacionService'
import BordeDetalle from '../ui/BordeDetalle'
import styles from './PublicacionDetalle.module.css'

const PublicacionDetalle = () => {
  const { loginDataContext: { idUsuario, tipoUsuario, empid } = {} } = useAuth()

  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [publicacion, setPublicacion] = useState(null)
  const [reputacion, setReputacion] = useState(0)
  const [calificacion, setCalificacion] = useState(0)
  const [mensajes, setMensajes] = useState([])
  const [datosFiscales, setDatosFiscales] = useState([])
  const [provincias, setProvincias] = useState([])
  const [deshabilitaCompra, setDeshabilitaCompra] = useState(false)
  const { setCantidadCompra } = useContext(CarritoContext)

  const buscarMensajes = async () => {
    const { data } = await mensajesService.buscarPorPublicacion(id)
    const mapa = {}
    data.forEach((msj) => {
      if (!msj.idMensajePadre) mapa[msj.idMensaje] = { ...msj }
    })
    data.forEach((msj) => {
      if (
        msj.idMensajePadre &&
        mapa[msj.idMensajePadre] &&
        !mapa[msj.idMensajePadre].idMensajeRta
      ) {
        mapa[msj.idMensajePadre].idMensajeRta = msj.idMensaje
        mapa[msj.idMensajePadre].textoRta = msj.texto
        mapa[msj.idMensajePadre].fechaEscritoRta = msj.fechaEscrito
        if (msj.idsAdjuntos) mapa[msj.idMensajePadre].idsAdjuntosRta = msj.idsAdjuntos
      }
    })
    const roots = Object.values(mapa).sort((a, b) => {
      const aMia = esMensajeDeMiEmpresa(a, idUsuario, empid) ? 1 : 0
      const bMia = esMensajeDeMiEmpresa(b, idUsuario, empid) ? 1 : 0
      if (bMia !== aMia) return bMia - aMia
      return dayjs(b.fechaEscrito).diff(dayjs(a.fechaEscrito))
    })
    setMensajes(roots.filter((mensaje) => !mensaje.privado))
  }

  const obtenerPublicacion = async () => {
    const { data } = await publicacionesService.buscarDetallePorId(Number(id))
    const publicacionData = { ...data.body, empid: data.body.vendedor?.empId }
    const tieneMedioDePago = publicacionData.mediosPagos?.some((mp) => mp.habilitado)
    const tieneMedioEnvio = publicacionData.mediosEnvios?.some((me) => me.habilitado)
    if (!tieneMedioDePago || !tieneMedioEnvio) {
      setDeshabilitaCompra(true)
    }
    setPublicacion(publicacionData)

    const reputacionRes = await reputacionService.buscarReputacion(publicacionData.idVendedor)
    setReputacion(reputacionRes.data)

    const calificacionRes = await calificacionService.buscarCalificacion(id)
    setCalificacion(calificacionRes.data)

    const datosFiscalesRes = await datosFiscalesService.buscarTodos(publicacionData.vendedor.empId)
    setDatosFiscales(datosFiscalesRes.data)

    const provinciasRes = await provinciasService.buscarProvinciasEnEBM()
    setProvincias(provinciasRes.data)

    await buscarMensajes()
  }

  const mostrarMensajeImpuestos = () => {
    if (!datosFiscales || datosFiscales.length === 0) {
      return false
    }
    return datosFiscales.some((df) => df.esAgentePercepcion === true)
  }

  const obtenerProvinciasPercepcion = () => {
    if (!datosFiscales || datosFiscales.length === 0 || !provincias || provincias.length === 0) {
      return []
    }

    const idsProvincias = datosFiscales
      .filter((df) => df.esAgentePercepcion === true)
      .map((df) => Number(df.idProvincia))

    const nombresProvincias = provincias
      .filter((p) => idsProvincias.includes(Number(p.idProvincia)))
      .map((p) => p.nombre)

    return nombresProvincias
  }

  useEffect(() => {
    obtenerPublicacion()
    setCantidadCompra(1)
  }, [id, idUsuario])

  const onCerrarClick = () => {
    navigate(-1)
    setCantidadCompra(1)
  }

  const buildCategoriaLinks = () => {
    if (!publicacion?.categoria?.root) return null
    try {
      const path = JSON.parse(publicacion.categoria.root)
      if (!Array.isArray(path) || path.length === 0) return null
      return path.map((cat, index) => ({
        id: cat.id,
        name: cat.name,
        onClick: () => {
          dispatch(
            setCategoriaPath(path.slice(0, index + 1).map((c) => ({ id: c.id, label: c.name })))
          )
          navigate('/Publicaciones', { state: { desdeCategoria: true } })
        }
      }))
    } catch {
      return null
    }
  }

  const normalizarEmpid = (valor) => (valor ?? '').toString().trim().toUpperCase()
  const reglas = useReglasPublicacion({ publicacion })

  if (!publicacion) {
    return <Spinner texto="Cargando publicación..." size="lg" />
  }

  const mostrarAviso = tipoUsuario !== 'R' && mostrarMensajeImpuestos()
  const infoVendedor = (
    <InformacionVendedor
      reputacion={reputacion.length > 0 ? reputacion : 0}
      publicacion={publicacion}
    />
  )

  return (
    <BordeDetalle
      titulo={`Categoría: ${publicacion?.categorias}`}
      categoriaLinks={buildCategoriaLinks()}
      onCerrarClick={() => onCerrarClick()}
    >
      <DetalleCabecera
        detalle={publicacion}
        reglasAviso={reglas}
        permiteComprar={
          reglas.permiteComprar &&
          publicacion?.idVendedor !== idUsuario &&
          normalizarEmpid(empid) !== normalizarEmpid(publicacion?.empid) &&
          publicacion?.idPublicacionEstado === ESTADO_PUBLICADA
        }
        calificacion={calificacion}
        onScrollCalificacion={() =>
          document.getElementById('calificacion-producto')?.scrollIntoView()
        }
        deshabilitaCompra={deshabilitaCompra}
      />
      <div className={`${styles.flexContainer} ${mostrarAviso ? '' : styles.sinAviso}`}>
        <div className={styles.columnaIzqArriba}>
          <Descripcion
            descripcion={publicacion?.descripcion}
            idPublicacion={publicacion?.idPublicacion}
            reglasAviso={reglas}
          />
        </div>
        {mostrarAviso && (
          <div className={styles.columnaDerArriba}>
            <div className={styles.noticeBox}>
              <strong>Importante:</strong> Este vendedor es agente de percepción en
              {obtenerProvinciasPercepcion().length > 0 && (
                <> {obtenerProvinciasPercepcion().join(', ')}</>
              )}
              . Al confirmar la compra vas a visualizar el cálculo correspondiente de impuestos.
            </div>
          </div>
        )}
        {!mostrarAviso && <div className={styles.columnaVendedor}>{infoVendedor}</div>}
        <div className={styles.columnaIzqAbajo}>
          <MensajeriaNew
            titulo={'Preguntas'}
            id={id}
            tipoDoc={'publicacion'}
            idVendedor={publicacion?.idVendedor}
            mensajes={mensajes}
            tipoMensajes={'preguntas'}
            usuario={idUsuario}
            empid={empid}
            idComprador={idUsuario}
            actualizarMensajes={buscarMensajes}
            idPublicacionEstado={publicacion?.idPublicacionEstado}
            empidVendedor={publicacion?.vendedor?.empresa?.empId}
          />
        </div>
        <div className={styles.columnaDerAbajo}>
          {mostrarAviso && infoVendedor}
          {calificacion.length > 0 && (
            <div id="calificacion-producto">
              <CalificacionProducto calificacion={calificacion} />
            </div>
          )}
        </div>
      </div>
    </BordeDetalle>
  )
}

export default PublicacionDetalle
